/** Tri, garde-fous et préparation : le brouillon rejoint l’éditeur humain existant. */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@artosera/ui';
import { assistantApi } from '../../api/assistant-reponse.js';
import type { Conversation, Message } from '../../api/boite-reception.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { useGesteAssistant } from './useGesteAssistant.js';
import styles from './Assistant.module.css';
import boite from './Boite.module.css';

export function ReperesAssistant({ c }: { c: Conversation }) {
  const { t } = useTranslation('regie');

  return (
    <span className={styles.reperes}>
      {c.assistant?.urgence === 'haute' && (
        <span className={styles.repere}>{t('assistantReponse.urgente')}</span>
      )}
      {c.assistant?.categorieProposee === 'indesirable' && (
        <span className={styles.repere}>{t('assistantReponse.indesirable')}</span>
      )}
      {c.sensible && <span className={styles.repere}>{t('assistantReponse.sensible')}</span>}
    </span>
  );
}

export function AssistantConversation({
  c,
  occupe,
  preparer,
}: {
  c: Conversation;
  occupe: boolean;
  preparer: (message: Message) => void;
}) {
  const { galerieSlug, galerie } = useShellContext();

  const { t } = useTranslation(['regie', 'commun']);

  const [motif, setMotif] = useState('');

  const ecrire = galerieALaction(galerie, 'inbox:write');

  const direction = galerieALaction(galerie, 'inbox:settings');

  const geste = useGesteAssistant((action: 'trier' | 'brouillon' | 'lever-sensible', appel) =>
    assistantApi.conversation(
      galerieSlug,
      c.id,
      action,
      { version: c.version, ...(action === 'lever-sensible' ? { motif: motif.trim() } : {}) },
      appel,
    ),
  );

  const categorie = c.categorie ?? c.assistant?.categorieProposee;

  return (
    <section className={styles.bandeau} aria-labelledby="assistant-conversation">
      <h3 id="assistant-conversation">{t('regie:assistantReponse.assistant')}</h3>
      {c.assistant && (
        <>
          <p>
            {categorie === 'indesirable'
              ? t('regie:assistantReponse.indesirable')
              : categorie
                ? t(`regie:boiteReception.categories.${categorie}`)
                : t('regie:boiteReception.sansCategorie')}
            {c.categorieSource === 'assistant' && ` · ${t('regie:assistantReponse.proposee')}`}
          </p>
          <p>
            {t('regie:assistantReponse.urgence')} :{' '}
            {t(`regie:assistantReponse.urgences.${c.assistant.urgence}`)}
          </p>
          <p className={boite.texte}>{c.assistant.resume}</p>
          <p>{t(`regie:assistantReponse.tri.${c.assistant.etat}`)}</p>
          {c.assistant.etat === 'echec' && ecrire && (
            <Button
              variant="secondary"
              disabled={geste.pending || occupe}
              onClick={() => void geste.executer('trier')}
            >
              {t('regie:assistantReponse.retrier')}
            </Button>
          )}
        </>
      )}
      {c.sensible && (
        <>
          <h4>{t('regie:assistantReponse.sensible')}</h4>
          <ul>
            {c.motifsSensibles?.map((m) => (
              <li key={m}>
                {t(`regie:assistantReponse.motifs.${m}`, {
                  defaultValue: t('regie:assistantReponse.motifs.assistant'),
                })}
              </li>
            ))}
          </ul>
          <p id="assistant-sensible-aide">{t('regie:assistantReponse.sensibleAide')}</p>
          {direction && (
            <form
              className={boite.formulaire}
              onSubmit={(e) => {
                e.preventDefault();
                if (motif.trim() && !geste.pending) void geste.executer('lever-sensible');
              }}
            >
              <Field htmlFor="assistant-motif" label={t('regie:assistantReponse.motifLevee')}>
                <Input
                  id="assistant-motif"
                  required
                  maxLength={500}
                  value={motif}
                  onChange={(e) => setMotif(e.target.value)}
                />
              </Field>
              <Button
                type="submit"
                variant="secondary"
                disabled={!motif.trim() || geste.pending || occupe}
              >
                {t('regie:assistantReponse.lever')}
              </Button>
            </form>
          )}
        </>
      )}
      {ecrire && !c.contactEfface && (
        <Button
          variant="secondary"
          disabled={c.sensible || geste.pending || occupe}
          aria-describedby={c.sensible ? 'assistant-sensible-aide' : undefined}
          onClick={() => {
            void geste.executer('brouillon').then((r) => {
              if (r?.message) preparer(r.message);
            });
          }}
        >
          {t('regie:assistantReponse.preparer')}
        </Button>
      )}
      {geste.pending && <p role="status">{t('regie:assistantReponse.attente')}</p>}
      {geste.erreur && <p role="alert">{geste.erreur}</p>}
    </section>
  );
}
